import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Shield, Crown, Star, Home, Building2, Warehouse,
  CheckCircle2, AlertTriangle, Info, Save, Loader2,
  Calculator, TrendingUp, TrendingDown, RotateCcw,
  Sparkles, Heart, BookOpen,
} from 'lucide-react';

import { laudoApi } from '../../services/laudoApi';

/**
 * ============================================================
 * COMPONENTE: VistoriaInterna_AuditorAtivos
 * ------------------------------------------------------------
 * Auditoria de ativos construtivos por tipologia.
 *
 * LÓGICA:
 *   Fator final = BASE da tipologia
 *              ± penalidade por ausência de ativo obrigatório
 *              ± bônus por presença de ativo opcional
 *
 * O fator alimenta o cálculo de homogeneização da amostra
 * correspondente (NBR 14653-2).
 *
 * VERSÃO: 2.0.0
 * ============================================================
 */

/* =========================
   MATRIZ DE ATIVOS POR TIPOLOGIA
========================= */

const MATRIZ_ATIVOS = Object.freeze({
  ALTO: {
    label: 'Alto Padrão',
    descricao: 'Condomínios de luxo, edifícios gourmet',
    icon: Crown,
    cor: 'text-amber-400',
    base: 1.30,
    itens: [
      { id: 'auto',   label: 'Automação Central',  delta: -0.05, obrigatorio: true  },
      { id: 'elev',   label: 'Elevador Privativo', delta: -0.10, obrigatorio: true  },
      { id: 'clima',  label: 'Climatização VRF',   delta: -0.05, obrigatorio: true  },
      { id: 'pisc',   label: 'Piscina Privativa',  delta:  0.03, obrigatorio: false },
      { id: 'acad',   label: 'Academia / Spa',     delta:  0.02, obrigatorio: false },
      { id: 'seg24',  label: 'Segurança 24h',      delta:  0.03, obrigatorio: false },
    ],
  },
  MEDIO_ALTO: {
    label: 'Médio-Alto',
    descricao: 'Condomínios clube, casas amplas',
    icon: Star,
    cor: 'text-orange-400',
    base: 1.15,
    itens: [
      { id: 'gourmet', label: 'Área Gourmet',        delta: -0.05, obrigatorio: true  },
      { id: 'garagem', label: 'Garagem Coberta (2+)',delta: -0.05, obrigatorio: true  },
      { id: 'clima',   label: 'Ar Condicionado',     delta:  0.03, obrigatorio: false },
      { id: 'pisc',    label: 'Piscina Coletiva',    delta:  0.02, obrigatorio: false },
    ],
  },
  MEDIO: {
    label: 'Médio',
    descricao: 'Construções integradas e regulares',
    icon: Home,
    cor: 'text-blue-400',
    base: 1.00,
    itens: [
      { id: 'gourmet', label: 'Área Gourmet',     delta:  0.05, obrigatorio: false },
      { id: 'garagem', label: 'Garagem Coberta',  delta:  0.05, obrigatorio: false },
      { id: 'portao',  label: 'Portão Automático',delta:  0.02, obrigatorio: false },
    ],
  },
  ECONOMICO: {
    label: 'Econômico',
    descricao: 'Habitações coletivas / populares',
    icon: Building2,
    cor: 'text-emerald-400',
    base: 0.90,
    itens: [
      { id: 'portao',  label: 'Portão Automático', delta:  0.03, obrigatorio: false },
      { id: 'reforma', label: 'Reforma Recente',   delta:  0.05, obrigatorio: false },
    ],
  },
  POPULAR: {
    label: 'Popular',
    descricao: 'Construções simples / antigas',
    icon: Warehouse,
    cor: 'text-slate-400',
    base: 0.80,
    itens: [
      { id: 'reforma', label: 'Reforma Recente',   delta:  0.03, obrigatorio: false },
      { id: 'muro',    label: 'Muro / Cerca',      delta: -0.03, obrigatorio: true  },
    ],
  },
});

/* =========================
   COMPONENTE
========================= */

export default function VistoriaInterna_AuditorAtivos({
  reportNumber = 'REPORT-2026-MH-001',
  peritoAtivo = 'MONTE_HERMOM_USER',
  tipologia: tipologiaControlada,
  selecionados: selecionadosControlados,
  onChange,
  onPersistido,
}) {
  const [tipologia, setTipologia] = useState(tipologiaControlada || 'MEDIO');
  const [selecionados, setSelecionados] = useState(selecionadosControlados || {});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);

  const config = MATRIZ_ATIVOS[tipologia];

  /* ---------- sincronização com props ---------- */
  useEffect(() => {
    if (tipologiaControlada && tipologiaControlada !== tipologia) {
      setTipologia(tipologiaControlada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipologiaControlada]);

  useEffect(() => {
    if (selecionadosControlados && selecionadosControlados !== selecionados) {
      setSelecionados(selecionadosControlados);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionadosControlados]);

  /* ---------- emite mudanças ---------- */
  const notificar = useCallback((novaTipologia, novaSelecao) => {
    if (typeof onChange === 'function') {
      onChange({
        tipologia: novaTipologia,
        selecionados: novaSelecao,
        fatorFinal: calcularFatorFinal(novaTipologia, novaSelecao),
      });
    }
  }, [onChange]);

  const trocarTipologia = useCallback((nova) => {
    if (nova === tipologia) return;
    setTipologia(nova);
    setSelecionados({});
    setErro(null);
    notificar(nova, {});
  }, [tipologia, notificar]);

  const toggleItem = useCallback((itemId) => {
    setSelecionados(prev => {
      const nova = { ...prev, [itemId]: !prev[itemId] };
      notificar(tipologia, nova);
      return nova;
    });
  }, [tipologia, notificar]);

  const resetar = useCallback(() => {
    setSelecionados({});
    setErro(null);
    setMensagem('Auditoria zerada.');
    notificar(tipologia, {});
    setTimeout(() => setMensagem(null), 2000);
  }, [tipologia, notificar]);

  /* ---------- cálculo do fator ---------- */
  const detalhamento = useMemo(() => {
    let fator = config.base;
    const itens = config.itens.map(item => {
      const presente = !!selecionados[item.id];
      let aplicado = 0;

      if (item.obrigatorio && !presente) {
        aplicado = item.delta; // penalidade
      } else if (!item.obrigatorio && presente) {
        aplicado = item.delta; // bônus
      }

      fator += aplicado;
      return { ...item, presente, aplicado };
    });

    const faltando = itens.filter(i => i.obrigatorio && !i.presente).length;
    return {
      base: config.base,
      itens,
      faltando,
      fator: +fator.toFixed(4),
      fatorPercentual: +((fator - 1) * 100).toFixed(2),
    };
  }, [config, selecionados]);

  /* ---------- persistir ---------- */
  const persistir = useCallback(async () => {
    setSalvando(true);
    setErro(null);
    setMensagem(null);

    try {
      await laudoApi.salvarModulo({
        report_id: reportNumber,
        perito: peritoAtivo,
        modulo: 'AUDITORIA_ATIVOS',
        timestamp: new Date().toISOString(),
        tipologia,
        selecionados,
        fatorFinal: detalhamento.fator,
        base: detalhamento.base,
      });
      setMensagem('Auditoria salva com sucesso.');
      setTimeout(() => setMensagem(null), 3000);
      if (typeof onPersistido === 'function') {
        onPersistido({ tipologia, selecionados, fatorFinal: detalhamento.fator });
      }
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente.');
      } else {
        setErro(err.message || 'Falha ao salvar.');
      }
    } finally {
      setSalvando(false);
    }
  }, [tipologia, selecionados, reportNumber, peritoAtivo, detalhamento, onPersistido]);

  /* ---------- render ---------- */

  const IconeTipologia = config.icon;

  return (
    <div className="bg-slate-900 rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl text-slate-200">

      {/* HEADER */}
      <header className="bg-slate-950 p-6 lg:p-8 border-b border-white/5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl bg-slate-900 border border-white/5 flex items-center justify-center shrink-0`}>
              <Sparkles size={22} className={config.cor} />
            </div>
            <div>
              <span className="text-[9px] text-slate-500 tracking-[0.4em] block">
                AUDITORIA DE ATIVOS · NBR 14653
              </span>
              <h2 className="text-white text-[16px] tracking-tight leading-tight mt-0.5">
                Fator de Homogeneização por Ativos
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={resetar}
            disabled={salvando}
            className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 text-[10px] tracking-widest flex items-center gap-2 transition-all disabled:opacity-30"
          >
            <RotateCcw size={12} />
            LIMPAR
          </button>
        </div>

        {/* SELETOR DE TIPOLOGIA */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(MATRIZ_ATIVOS).map(([chave, cfg]) => {
            const Icone = cfg.icon;
            const ativo = tipologia === chave;
            return (
              <button
                key={chave}
                type="button"
                onClick={() => trocarTipologia(chave)}
                disabled={salvando}
                className={`px-4 py-3 rounded-2xl text-[10px] tracking-widest flex items-center gap-2 transition-all ${
                  ativo
                    ? 'bg-amber-400 text-slate-950 shadow-lg'
                    : 'bg-slate-900 border border-white/10 text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <Icone size={14} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* DESCRIÇÃO DA TIPOLOGIA */}
        <div className="mt-4 flex items-center gap-3 text-slate-500 text-[10px] tracking-wider normal-case">
          <BookOpen size={12} />
          <span>{config.descricao}</span>
        </div>
      </header>

      {/* ERRO / MENSAGEM */}
      {erro && (
        <div className="bg-red-950/50 border-y border-red-500/20 px-6 lg:px-8 py-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <span className="text-red-200 text-[11px] tracking-wide leading-relaxed normal-case flex-1">
            {erro}
          </span>
        </div>
      )}
      {mensagem && (
        <div className="bg-emerald-950/50 border-y border-emerald-500/20 px-6 lg:px-8 py-4 flex items-center gap-3">
          <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
          <span className="text-emerald-200 text-[11px] tracking-wide normal-case">
            {mensagem}
          </span>
        </div>
      )}

      {/* LISTA DE ATIVOS */}
      <div className="p-6 lg:p-8 space-y-3">
        {detalhamento.itens.map((item) => {
          const isBonus = !item.obrigatorio;
          const aplicadoPositivo = item.aplicado > 0;
          const aplicadoNegativo = item.aplicado < 0;

          return (
            <label
              key={item.id}
              className={`flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl border cursor-pointer transition-all ${
                item.presente
                  ? 'bg-amber-500/5 border-amber-500/30'
                  : 'bg-slate-950 border-white/5 hover:border-white/10'
              } ${salvando ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {/* CHECKBOX */}
              <div className="flex items-center gap-4 flex-1 min-w-[240px]">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  item.presente
                    ? 'bg-amber-400 text-slate-950'
                    : 'bg-slate-900 border border-white/10'
                }`}>
                  {item.presente ? <CheckCircle2 size={16} /> : null}
                </div>

                <input
                  type="checkbox"
                  checked={item.presente}
                  onChange={() => toggleItem(item.id)}
                  className="hidden"
                  disabled={salvando}
                />

                <div className="min-w-0">
                  <span className="text-white text-[12px] tracking-wide block">
                    {item.label}
                  </span>
                  <span className={`text-[9px] tracking-widest ${
                    item.obrigatorio ? 'text-red-400/70' : 'text-emerald-400/70'
                  }`}>
                    {item.obrigatorio ? 'EXPECTATIVA MÍNIMA' : 'DIFERENCIAL OPCIONAL'}
                  </span>
                </div>
              </div>

              {/* DELTA APLICADO */}
              <div className="text-right min-w-[100px]">
                <span className="text-[9px] text-slate-500 tracking-widest block">
                  DELTA
                </span>
                <span className={`text-[13px] font-mono ${
                  aplicadoPositivo
                    ? 'text-emerald-400'
                    : aplicadoNegativo
                      ? 'text-red-400'
                      : 'text-slate-600'
                }`}>
                  {item.aplicado === 0
                    ? '—'
                    : `${aplicadoPositivo ? '+' : ''}${item.aplicado.toFixed(2)}`}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      {/* PAINEL DE RESULTADO */}
      <section className="bg-[#2F353B] p-6 lg:p-8 border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* BASE */}
          <div className="bg-slate-950 rounded-2xl p-5 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <IconeTipologia size={12} className={config.cor} />
              <span className="text-[9px] text-slate-500 tracking-[0.3em]">
                BASE {config.label.toUpperCase()}
              </span>
            </div>
            <span className="text-white text-[24px] font-mono tracking-tight">
              {detalhamento.base.toFixed(2)}
            </span>
          </div>

          {/* FALTANDO */}
          <div className={`rounded-2xl p-5 border ${
            detalhamento.faltando > 0
              ? 'bg-red-950/30 border-red-500/30'
              : 'bg-slate-950 border-white/5'
          }`}>
            <span className="text-[9px] text-slate-500 tracking-[0.3em] block mb-2">
              ATIVOS OBRIGATÓRIOS FALTANDO
            </span>
            <span className={`text-[24px] font-mono tracking-tight ${
              detalhamento.faltando > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {detalhamento.faltando}
            </span>
          </div>

          {/* FATOR FINAL */}
          <div className="bg-amber-400 rounded-2xl p-5 shadow-lg">
            <span className="text-[9px] text-slate-950/60 tracking-[0.3em] block mb-2">
              FATOR FINAL
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-slate-950 text-[32px] font-mono tracking-tighter leading-none">
                {detalhamento.fator.toFixed(4)}
              </span>
              <span className="text-slate-950/60 text-[12px] font-mono">
                ({detalhamento.fatorPercentual >= 0 ? '+' : ''}{detalhamento.fatorPercentual}%)
              </span>
            </div>
          </div>
        </div>

        {/* FÓRMULA EXPANDIDA */}
        <div className="mt-6 bg-slate-950 rounded-2xl p-4 border border-white/5">
          <div className="flex items-start gap-4">
            <Calculator size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-2 flex-1 min-w-0">
              <span className="text-[9px] text-amber-400 tracking-[0.3em] block">
                MEMÓRIA DE CÁLCULO
              </span>
              <p className="text-slate-400 text-[11px] font-mono normal-case break-all">
                {detalhamento.base.toFixed(2)}
                {detalhamento.itens
                  .filter(i => i.aplicado !== 0)
                  .map(i => ` ${i.aplicado > 0 ? '+' : '−'} ${Math.abs(i.aplicado).toFixed(2)}`)
                  .join('')}
                {' = '}
                <span className="text-amber-400">{detalhamento.fator.toFixed(4)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* AÇÃO */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={persistir}
            disabled={salvando}
            className="bg-amber-400 text-slate-950 px-8 py-4 rounded-2xl text-[11px] tracking-widest flex items-center gap-3 hover:bg-white transition-all disabled:opacity-50"
          >
            {salvando ? (
              <><Loader2 size={16} className="animate-spin" /> SALVANDO...</>
            ) : (
              <><Save size={16} /> SALVAR AUDITORIA</>
            )}
          </button>
        </div>
      </section>

      {/* RODAPÉ NORMATIVO */}
      <footer className="p-6 lg:p-8 bg-slate-950 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield size={14} className="text-amber-400/60" />
          <span className="text-[9px] text-slate-500 tracking-[0.3em]">
            FATOR FUNDAMENTADO NA PRESENÇA/AUSÊNCIA DE ATIVOS CRÍTICOS
          </span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-[9px] tracking-widest font-mono">
          <FileText size={12} />
          <span>ID: {reportNumber}</span>
        </div>
      </footer>
    </div>
  );
}

/* =========================
   HELPERS EXPORTÁVEIS
========================= */

export function calcularFatorFinal(tipologia, selecionados = {}) {
  const config = MATRIZ_ATIVOS[tipologia];
  if (!config) return 1.00;

  let fator = config.base;
  config.itens.forEach(item => {
    const presente = !!selecionados[item.id];
    if (item.obrigatorio && !presente) fator += item.delta;
    if (!item.obrigatorio && presente) fator += item.delta;
  });

  return +fator.toFixed(4);
}

export { MATRIZ_ATIVOS };